import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, CloudServerOutlined, DatabaseOutlined, UnorderedListOutlined, InfoCircleOutlined } from '@ant-design/icons';
import SideModal from '../../components/shared/SideModal/SideModal';

const Resources: React.FC = () => {
  const navigate = useNavigate();
  const [showHelpModal, setShowHelpModal] = useState(false);

  return (
    <div className="max-w-[1400px] mx-auto px-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Resources</h1>
            <button
              onClick={() => setShowHelpModal(true)}
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="Get help about Resources"
            >
              <InfoCircleOutlined className="text-lg" />
            </button>
          </div>
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Cloud Infrastructure Management</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Manage all your cloud resources in one place. Create and configure WordPress sites, virtual machines, databases, and other infrastructure components to power your applications.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* WordPress */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
          <div className="mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0073aa] to-[#00a0d2] rounded-lg flex items-center justify-center shadow-md">
              <svg 
                viewBox="0 0 122.52 122.523" 
                className="w-7 h-7 fill-white"
              >
                <g>
                  <path d="M8.708,61.26c0,20.802,12.089,38.779,29.619,47.298L13.258,39.872C10.25,46.883,8.708,53.865,8.708,61.26z M87.161,58.699c0-6.495-2.333-10.993-4.334-14.494c-2.664-4.329-5.161-7.995-5.161-12.324c0-4.831,3.664-9.328,8.825-9.328c0.233,0,0.454,0.029,0.681,0.042c-9.35-8.566-21.807-13.796-35.489-13.796c-18.36,0-34.513,9.42-43.91,23.688c1.233,0.037,2.395,0.063,3.382,0.063c5.497,0,14.006-0.667,14.006-0.667c2.833-0.167,3.167,3.994,0.337,4.329c0,0-2.847,0.335-6.015,0.501l19.138,56.925l11.501-34.493l-8.188-22.434c-2.83-0.166-5.511-0.501-5.511-0.501c-2.832-0.166-2.5-4.496,0.332-4.329c0,0,8.679,0.667,13.843,0.667c5.496,0,14.006-0.667,14.006-0.667c2.835-0.167,3.168,3.994,0.337,4.329c0,0-2.853,0.335-6.015,0.501l18.992,56.494l5.242-17.517C85.503,67.299,87.161,63.219,87.161,58.699z M62.265,65.483l-15.765,45.832c4.71,1.385,9.687,2.134,14.846,2.134c6.12,0,11.989-1.058,17.452-2.979c-0.141-0.225-0.269-0.464-0.374-0.724L62.265,65.483z M107.164,36.234c0.226,1.674,0.354,3.471,0.354,5.404c0,5.333-0.996,11.328-3.996,18.824l-16.053,46.413c15.624-9.111,26.133-26.038,26.133-45.426C113.601,52.612,111.378,43.989,107.164,36.234z"/>
                  <path d="M61.26,0C27.486,0,0,27.486,0,61.26c0,33.775,27.486,61.263,61.26,61.263c33.775,0,61.263-27.488,61.263-61.263C122.523,27.486,95.035,0,61.26,0z M61.26,119.715c-32.23,0-58.453-26.223-58.453-58.455c0-32.23,26.223-58.451,58.453-58.451c32.234,0,58.456,26.221,58.456,58.451C119.716,93.492,93.494,119.715,61.26,119.715z"/>
                </g>
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">WordPress Sites</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Managed WordPress hosting</p>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/resources/create-wordpress')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg text-sm font-medium hover:bg-dark-bg transition-colors"
            >
              <PlusOutlined />
              Create New
            </button>
            <button
              onClick={() => navigate('/wordpress/environments')}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="View all WordPress environments"
            >
              <UnorderedListOutlined />
            </button>
          </div>
        </div>

        {/* Virtual Machines */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
          <CloudServerOutlined className="text-3xl text-blue-600 mb-3" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Virtual Machines</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Deploy and manage VMs</p>
          <button
            onClick={() => navigate('/resources/create-vm')}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg text-sm font-medium hover:bg-dark-bg transition-colors w-full justify-center"
          >
            <PlusOutlined />
            Create VM
          </button>
        </div>

        {/* Databases */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
          <DatabaseOutlined className="text-3xl text-purple-600 mb-3" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Databases</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Managed database instances</p>
          <button
            onClick={() => navigate('/resources/create-database')}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg text-sm font-medium hover:bg-dark-bg transition-colors w-full justify-center"
          >
            <PlusOutlined />
            Create Database
          </button>
        </div>
      </div>

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="About Resources"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">What are Resources?</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Resources are the fundamental building blocks of your cloud infrastructure. This section provides access to 
              all the core services you need to build and run your applications, including WordPress sites, virtual machines, 
              and databases.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Available Resources</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>WordPress Sites:</strong> Managed WordPress hosting with automatic updates and backups</li>
              <li><strong>Virtual Machines:</strong> Deploy VMs with your preferred operating system and configuration</li>
              <li><strong>Databases:</strong> Managed database instances including PostgreSQL, MySQL, MongoDB, and more</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Getting Started</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              To create a new resource, click on the "Create New" or "Create VM" / "Create Database" button on the
              corresponding resource card. Each resource type has its own configuration wizard to guide you through
              the setup process.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              All resources are automatically provisioned and ready to use once created. You can manage, monitor, 
              and scale your resources from this central location.
            </p>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default Resources;